import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../App.css';
import api from '../../utils/api.js';
import { handleApiError, userFacingDetail } from '../../utils/errorHandler.js';
import FileUploadSection from '../../components/FileUploadSection.js';
import { PriorityBadge, PriorityToggle } from '../../components/PriorityBadge.js';
import PromptSelectModal from '../../components/PromptSelectModal';
import AiResultModal from '../../components/AiResultModal.js';
import useNavigateBack from '../../utils/useNavigateBack.js';
import SkeletonLoader from '../../components/SkeletonLoader.js';
import { formatToJST } from '../../utils/dateFormatter.js';

function ProposedJobDetailPage() {
    const { jobId } = useParams();
    const navigate = useNavigate();

    const [jobData, setJobData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // --- 編集モード用State ---
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState(null);

    // --- タブ用State ---
    const [activeTab, setActiveTab] = useState('basic');

    // --- AI処理用State ---
    const [recommendationLoading, setRecommendationLoading] = useState(false);
    const [recommendationResult, setRecommendationResult] = useState(null);
    const [recommendationError, setRecommendationError] = useState(null);
    const [showPromptSelectModal, setShowPromptSelectModal] = useState(false);

    const fetchJobData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.fetch(`/proposed_jobs/${jobId}`);
            if (!response) return; // Redirected
            if (!response.ok) throw new Error('求人提案データの取得に失敗しました。');
            const data = await response.json();
            setJobData(data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    }, [jobId]);

    useEffect(() => {
        fetchJobData();
    }, [fetchJobData]);

    const handleBackClick = useNavigateBack(
        jobData?.opportunity_id
            ? `/opportunity/${jobData.opportunity_id}?tab=proposed_jobs`
            : '/'
    );

    const handleEditDoubleClick = () => {
        setIsEditing(true);
        setEditData({ ...jobData });
        setEditError(null);
    };

    const handleEditChange = (e, field) => {
        setEditData(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleSaveEdit = async () => {
        if (!editData.company_name.trim()) {
            setEditError('企業名は必須です。');
            return;
        }
        setEditLoading(true);
        setEditError(null);
        try {
            const response = await api.fetch(`/proposed_jobs/${jobId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    company_name: editData.company_name,
                    job_description: editData.job_description,
                    memo: editData.memo,
                    priority: editData.priority || null
                }),
            });
            if (!response) return; // Redirected
            if (!response.ok) {
                const errData = await response.json().catch(() => null);
                throw new Error(userFacingDetail(response.status, errData) || '更新に失敗しました。');
            }
            const updatedJob = await response.json();
            setJobData(updatedJob);
            setIsEditing(false);
        } catch (err) {
            setEditError(err.message);
        } finally {
            setEditLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditError(null);
    };

    const handleDelete = async () => {
        setEditLoading(true);
        try {
            const response = await api.fetch(`/proposed_jobs/${jobId}`, {
                method: 'DELETE',
            });
            if (!response) return; // Redirected
            if (!response.ok) {
                throw new Error('削除に失敗しました。');
            }
            setShowDeleteConfirm(false);
            alert('求人提案を削除しました。');
            if (jobData.opportunity_id) {
                navigate(`/opportunity/${jobData.opportunity_id}`);
            } else {
                navigate('/');
            }
        } catch (err) {
            alert(err.message);
        } finally {
            setEditLoading(false);
        }
    };

    // ---------------------------------------------
    // AI推薦文生成関連のハンドラ
    // ---------------------------------------------

    /**
     * 「AI推薦文を生成」ボタンがクリックされたときの処理
     * プロンプト選択モーダルを表示する
     */
    const handleClickGenerateRecommendation = () => {
        if (recommendationLoading || isEditing) {
            return;
        }
        setShowPromptSelectModal(true);
    };

    /**
     * プロンプト選択後、直接AI推薦文を生成する
     */
    const handlePromptSelected = (promptId) => {
        setShowPromptSelectModal(false);
        executeGenerateRecommendation(promptId || null);
    };

    /**
     * 実際のAI推薦文生成処理（ポップアップ表示）
     */
    const executeGenerateRecommendation = async (promptId) => {
        if (recommendationLoading || isEditing) {
            return;
        }

        setRecommendationError(null);
        setRecommendationLoading(true);

        try {
            const params = new URLSearchParams();
            params.append('job_id', jobId);
            if (promptId) params.append('prompt_id', promptId);
            const url = `/opportunities/${jobData.opportunity_id}/generate_recommendation?${params.toString()}`;

            const response = await api.fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response) return;

            if (!response.ok) {
                const errorMessage = await handleApiError(response, '推薦文の生成に失敗しました。');
                throw new Error(errorMessage);
            }

            const data = await response.json();
            setRecommendationResult(data.recommendation);

        } catch (err) {
            console.error(err);
            setRecommendationError(err.message);
        } finally {
            setRecommendationLoading(false);
        }
    };

    // ---------------------------------------------
    // タブコンテンツのレンダリング
    // ---------------------------------------------
    const renderTabContent = () => {
        switch (activeTab) {
            case 'basic':
                return (
                    <div className="detail-single-column-container">
                        {/* --- 求人内容 --- */}
                        <div className="detail-item" onDoubleClick={handleEditDoubleClick}>
                            <span className="detail-label">求人内容</span>
                            {isEditing ? (
                                <textarea
                                    className="detail-value-textarea"
                                    rows="10"
                                    value={editData.job_description || ''}
                                    onChange={(e) => handleEditChange(e, 'job_description')}
                                />
                            ) : (
                                <pre className="detail-value-pre">{jobData.job_description || '(求人内容がありません)'}</pre>
                            )}
                        </div>

                        {/* --- 求人票 (編集モード以外で表示) --- */}
                        {!isEditing && (
                            <FileUploadSection
                                uploadUrlEndpoint={`/proposed_jobs/${jobId}/job_posting/upload_url`}
                                confirmEndpoint={`/proposed_jobs/${jobId}/job_posting/confirm`}
                                getEndpoint={`/proposed_jobs/${jobId}/job_posting`}
                                deleteEndpoint={`/proposed_jobs/${jobId}/job_posting`}
                                label="求人票"
                                accept="application/pdf,text/plain,text/csv,.txt,.csv"
                                maxFileSizeMB={10}
                            />
                        )}

                        {/* --- 優先度 --- */}
                        <div className="detail-item" onDoubleClick={handleEditDoubleClick}>
                            <span className="detail-label">優先度</span>
                            {isEditing ? (
                                <PriorityToggle
                                    value={editData.priority}
                                    onChange={(v) => setEditData(prev => ({ ...prev, priority: v }))}
                                />
                            ) : (
                                <PriorityBadge priority={jobData.priority} />
                            )}
                        </div>

                        {/* --- 登録日時 --- */}
                        <div className="detail-item">
                            <span className="detail-label">登録日時</span>
                            <span className="detail-value">{formatToJST(jobData.created_at)}</span>
                        </div>
                    </div>
                );

            case 'memo':
                return (
                    <React.Fragment>
                        <div className="detail-item-full" onDoubleClick={handleEditDoubleClick}>
                            <span className="detail-label">メモ</span>
                            {isEditing ? (
                                <textarea
                                    className="detail-value-textarea"
                                    rows="20"
                                    value={editData.memo || ''}
                                    onChange={(e) => handleEditChange(e, 'memo')}
                                />
                            ) : (
                                <pre className="detail-value-pre">{jobData.memo || '(メモがありません)'}</pre>
                            )}
                        </div>
                    </React.Fragment>
                );

            default:
                return null;
        }
    };

    if (loading) return <SkeletonLoader variant="detail" rows={4} />;
    if (error) return <p className="error-message">エラー: {error}</p>;
    if (!jobData) return null;

    return (
        <div>
            {/* AI推薦文ポップアップ（ローディング・結果・エラー） */}
            <AiResultModal
                loading={recommendationLoading}
                loadingMessage="AI推薦文を生成中..."
                result={recommendationResult}
                title="✨ AI推薦文"
                error={recommendationError}
                onClose={() => setRecommendationResult(null)}
                onErrorClose={() => setRecommendationError(null)}
            />

            {/* プロンプト選択モーダル */}
            <PromptSelectModal
                isOpen={showPromptSelectModal}
                onClose={() => setShowPromptSelectModal(false)}
                onConfirm={handlePromptSelected}
                promptType="recommendation"
                title="AI推薦文を生成"
                message="使用するプロンプトを選択してください"
                confirmButtonText="生成する"
            />

            <div className="detail-header">
                <div>
                    <h1 className="page-title">求人提案詳細</h1>
                    {jobData?.opportunity_applicant_name && (
                        <p className="breadcrumb-applicant">
                            求職者：
                            <a
                                href={`/opportunity/${jobData.opportunity_id}`}
                                onClick={(e) => { e.preventDefault(); navigate(`/opportunity/${jobData.opportunity_id}`); }}
                            >
                                {jobData.opportunity_applicant_name}
                            </a>
                        </p>
                    )}
                </div>
                <div className="header-actions">
                    {!isEditing && (
                        <>
                            <button
                                className="modal-button-confirm"
                                onClick={handleClickGenerateRecommendation}
                                disabled={recommendationLoading}
                                style={{ marginRight: '10px' }}
                            >
                                ✨ AI推薦文
                            </button>
                            <button className="btn-delete" onClick={() => setShowDeleteConfirm(true)} style={{ marginRight: '10px' }}>
                                削除
                            </button>
                            <button className="modal-button-cancel" onClick={handleBackClick}>
                                前ページに戻る
                            </button>
                        </>
                    )}
                </div>
            </div>

            {showDeleteConfirm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2 className="modal-title">削除の確認</h2>
                        <p style={{ textAlign: 'center', marginBottom: '25px' }}>
                            この求人提案を削除してもよろしいですか？
                        </p>
                        <div className="modal-actions">
                            <button className="modal-button-cancel" onClick={() => setShowDeleteConfirm(false)} disabled={editLoading}>
                                キャンセル
                            </button>
                            <button className="btn-delete" onClick={handleDelete} disabled={editLoading} style={{ margin: 0 }}>
                                {editLoading ? '処理中...' : '削除する'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="detail-card">

                {/* 企業名 (編集モード対応) */}
                <div className="detail-item" onDoubleClick={handleEditDoubleClick}>
                    <span className="detail-label">企業名</span>
                    {isEditing ? (
                        <input
                            type="text"
                            className="card-title-input"
                            value={editData.company_name}
                            onChange={(e) => handleEditChange(e, 'company_name')}
                            autoFocus
                        />
                    ) : (
                        <span className="detail-value-large" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '40ch', display: 'inline-block' }} title={jobData.company_name}>{jobData.company_name}</span>
                    )}
                </div>

                {/* タブナビゲーション */}
                <div className="tab-nav">
                    <button
                        className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
                        onClick={() => setActiveTab('basic')}
                        disabled={isEditing || recommendationLoading}
                    >
                        基本情報
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'memo' ? 'active' : ''}`}
                        onClick={() => setActiveTab('memo')}
                        disabled={isEditing || recommendationLoading}
                    >
                        メモ
                    </button>
                </div>

                {/* タブコンテンツ */}
                <div className="tab-content">
                    {renderTabContent()}
                </div>

            </div>

            {isEditing && (
                <div className="edit-actions-footer">
                    {editError && <p className="modal-error-message">{editError}</p>}
                    <div className="edit-actions-buttons">
                        <button className="modal-button-cancel" onClick={handleCancelEdit} disabled={editLoading}>
                            キャンセル
                        </button>
                        <button className="modal-button-confirm" onClick={handleSaveEdit} disabled={editLoading}>
                            {editLoading ? '保存中...' : '保存'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProposedJobDetailPage;
